import { Component, OnInit, Input, OnDestroy } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { BehaviorSubject, Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";

import { UserService } from "./../../../../../shared/services/user.service";
import { PractitionerService } from "./../../../../shared/services/practitioner.service";
import { NotificationsService } from "./../../../../../shared/services/notifications.service";
import { ViewStateService } from "./../../../../../shared/services/view-state.service";
import { AppSettingsService } from "./../../../../../shared/services/app-settings.service";
import { Course } from "./../../../../shared/models/course";
import { RegexPatterns } from "./../../../../../shared/regex/regex-patterns";
import { GlobalValidators } from "./../../../../../shared/validators/global-validators";
import { DateUtils } from "app/shared/utils/date-utils";


@Component({
  selector: "app-course-editor",
  templateUrl: "./course-editor.component.html",
  styleUrls: ["./course-editor.component.css"]
})
export class CourseEditorComponent implements OnInit, OnDestroy {
  @Input() course$: BehaviorSubject<Course>;

  private dateInputFormat = this.appSettings.IsCompatibilityMode
    ? "MM/DD/YYYY"
    : "YYYY-MM-DD";
  private ccpBegins: string;
  private ccpEnds: string;

  courseForm: UntypedFormGroup;
  formLevelErrors: string[] = [];
  personId: string;
  action = "Add";
  onDestroy$ = new Subject();
  amount='';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private viewState: ViewStateService,
    private practitioner: PractitionerService,
    private userService: UserService,
    private notifications: NotificationsService,
    private router: Router,
    public appSettings: AppSettingsService
  ) {}

  ngOnInit() {
    this.viewState.startLoading();
    this.personId = this.userService.CurrentUser.personId;
    this.getCcpDates();
    this.buildForm();
    if (this.course$) {
      this.course$
        .pipe(
          filter((c) => c !== null),
          takeUntil(this.onDestroy$)
        )
        .subscribe((c) => {
          this.action = "Update";
          this.fillFormIfEditing(c);
        });
    }
  }

  private fillFormIfEditing(course: Course) {
    if (course.completionDate) {
      course.completionDate = DateUtils.format(course.completionDate, this.dateInputFormat );
    }
    this.courseForm.patchValue(course);
    this.patchSpecialValues(course);
    this.viewState.stopLoading();
  }

  onSubmit(updatedFormValues: any) {
    this.viewState.startLoading();
    this.formValues = updatedFormValues;
    this.validateForm();
    this.submitCourseData();
  }

  onCancel(event: Event) {
    event.preventDefault();

    this.router.navigate(["practitioner/credentials-maintenance/default"]);
  }

  onAmountChange(value: string): void {
    const regex = /^\d*\.?\d{0,2}$/;
    if (regex.test(value)) {
      this.amount = value;
    } else {
      // Optionally trim to valid input
      const valid = value.match(/^(\d+(\.\d{0,2})?)?/);
      this.amount = valid ? valid[0] : '';
    }
  }

  private getMaxCourseEndDate() {
    const endDate = DateUtils.min(this.ccpEnds, new Date());
    return endDate;
  }


  private buildForm(): void {
    this.courseForm = this.formBuilder.group({
      id: [this.formValues.id],
      personId: [this.formValues.personId],
      courseName: [
        this.formValues.courseName,
        [Validators.required, Validators.maxLength(500)],
      ],
      courseUsageTypeId: [
        this.formValues.courseUsageTypeId,
        [Validators.required],
      ],
      courseCategoryId: [
        this.formValues.courseCategoryId,
        [Validators.required],
      ],
      courseHours: [
        this.formValues.courseHours,
        [
          Validators.required,
          Validators.maxLength(5),
          Validators.pattern(RegexPatterns.decimalsOnlyGTZero),
        ],
      ],
      completionDate: [this.formValues.completionDate],
    });
  }

  private submitCourseData() {
    if (this.courseForm.valid)
      {if (!this.course$) {
        this.practitioner
          .addCourse(this.personId, this.courseForm.value)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe(
            (_resp) => {
              this.notifications.success("Success", "The course was added.");
              this.viewState.stopLoading();
              this.router.navigate([
                "practitioner/credentials-maintenance/default",
              ]);
            },
            (errors) => {
              this.notifications.error("Failed", "The course was not added.");
              this.mapServerErrorsToForm(errors.error);
              this.viewState.stopLoading();
            }
          );
      } else {
        const courseData = Object.assign(
          this.courseForm.value,
          this.course$.value.id
        );
        this.practitioner
          .updateCourse(this.personId, courseData)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe(
            (_resp) => {
              this.notifications.success("Success", "The course was updated.");
              this.viewState.stopLoading();
              this.router.navigate([
                "practitioner/credentials-maintenance/default",
              ]);
            },
            (errors) => {
              this.notifications.error("Failed", "The course was not updated.");
              this.mapServerErrorsToForm(errors.error);
              this.viewState.stopLoading();
            }
          );
      }}
  }

  private validateForm() {
    for (const control in this.courseForm.controls) {
      this.courseForm.controls[control].updateValueAndValidity();
    }

    let hasErrors = false;
    for (const field in this.formErrors) {
      this.formErrors[field] = "";
      const control = this.courseForm.get(field);
      if (control && control.invalid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }
    if (hasErrors) {
      this.notifications.error(
        "Fields Invalid",
        "Please review the form and resubmit."
      );
      this.viewState.stopLoading();
    }
  }

  private mapServerErrorsToForm(errorObj: any) {
    const errors = errorObj;
    errors.map((error) => {
      this.formErrors[error.fieldName] = "";
      const control = this.courseForm.get(error.fieldName);
      this.formErrors[error.fieldName] += error.message + " ";
      if (!control) {
        this.formLevelErrors.push(error.message);
      }
    });
    this.viewState.stopLoading();
  }

  private patchSpecialValues(course: Course) {
    this.courseForm.get("courseCategoryId").patchValue(course.courseCategoryId);
    this.courseForm.get("id").patchValue(course.id);
  }

  private getCcpDates() {
    this.practitioner
      .getCCPInfo(this.personId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((info) => {
        this.ccpBegins = DateUtils.format(info.beginDate,this.dateInputFormat);
        this.ccpEnds = DateUtils.format(info.expirationDate,this.dateInputFormat);
        this.setupCompletionDateValidators();
        this.viewState.stopLoading();
      });
  }

  private setupCompletionDateValidators() {
    this.courseForm.controls["completionDate"].setValidators([
      Validators.required,
      GlobalValidators.validDate(this.dateInputFormat),
      GlobalValidators.dateBetween(
        new Date(this.ccpBegins),
        this.getMaxCourseEndDate(),
        this.dateInputFormat
      ),
    ]);
  }

  private formValues = {
    id: "",
    personId: this.userService.CurrentUser.personId.toString(),
    courseName: "",
    courseUsageTypeId: "",
    courseCategoryId: "",
    courseHours: "",
    completionDate: "",
  };

  formErrors = {
    courseName: "",
    courseUsageTypeId: "",
    courseCategoryId: "",
    courseHours: "",
    completionDate: "",
  };

  get validationMessages() {
    return {
      courseName: {
        required: "Course Name is required.",
        maxlength: "Course Name must be max 500 characters.",
      },
      courseUsageTypeId: {
        required: "Course Type is required.",
      },
      courseCategoryId: {
        required: "Course Category is required.",
      },
      courseHours: {
        required: "Hours are required.",
        pattern: "Hours must be numbers only.",
        maxlength:
          "Hours must be less than or equal to 5 digits including decimals.",
      },
      completionDate: {
        required: "Completion Date is required.",
        validDate: "Complete Date must be a valid date.",
        dateBetween: `Completion Date must be between ${DateUtils.format(this.ccpBegins,"MM/DD/YYYY")} 
                      and ${DateUtils.format(this.getMaxCourseEndDate(),"MM/DD/YYYY")}.`,
      },
    };
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
